import { Box, Button, createStyles, makeStyles, Modal, Slide, IconButton } from '@material-ui/core'
import React from 'react'
import { Colors, SvgIcon, Typography } from 'src/components/atoms'

export interface ModalPdfFontGuideProps {
  open: boolean
  onClose: () => void
}

const useStyles = makeStyles(() =>
  createStyles({
    modal: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    container: {
      width: '600px',
      maxWidth: '90vw',
      maxHeight: '90vh',
      background: Colors.functional.whiteText.default,
      borderRadius: 12,
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      boxShadow: Colors.functional.shadow.modal
    },
    header: {
      background: Colors.accent.keyPurple.default,
      color: Colors.functional.whiteText.default,
      padding: '24px 32px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    headerTitle: {
      fontSize: 18,
      fontWeight: 700
    },
    closeButton: {
      color: Colors.functional.whiteText.default,
      width: 24,
      height: 24,
      padding: 0
    },
    body: {
      padding: 32,
      overflowY: 'auto',
      flex: 1
    },
    section: {
      marginBottom: 32
    },
    sectionTitle: {
      fontWeight: 700,
      fontSize: 16,
      color: Colors.functional.background.dark,
      marginBottom: 12,
      paddingBottom: 8,
      borderBottom: `2px solid ${Colors.functional.border.lightGray}`
    },
    tipBox: {
      background: Colors.selected.background,
      borderLeft: `4px solid ${Colors.accent.keyPurple.default}`,
      padding: 16,
      borderRadius: 4,
      marginTop: 16
    },
    footer: {
      padding: '20px 32px',
      background: Colors.background.lightGray,
      borderTop: `1px solid ${Colors.functional.border.lightGray}`,
      display: 'flex',
      justifyContent: 'flex-end'
    },
    methodBox: {
      background: Colors.background.lightGray,
      border: `1px solid ${Colors.functional.border.lightGray}`,
      borderRadius: 8,
      marginBottom: 12,
      padding: 16
    },
    methodTitle: {
      fontWeight: 700,
      color: Colors.accent.keyPurple.default,
      marginBottom: 8,
      fontSize: 14
    },
    methodContent: {
      color: Colors.base.middleGray
    },
    sectionBody: {
      color: Colors.base.black
    },
    tipTitle: {
      fontWeight: 700,
      color: Colors.accent.keyPurple.default,
      marginBottom: 8
    },
    tipContent: {
      color: Colors.accent.keyPurple.default
    },
    closeButtonStyle: {
      background: Colors.accent.keyPurple.default,
      color: Colors.functional.whiteText.default
    }
  })
)

export const ModalPdfFontGuide: React.FC<ModalPdfFontGuideProps> = ({ open, onClose }) => {
  const classes = useStyles()

  return (
    <Modal open={open} onClose={onClose} className={classes.modal}>
      <Slide direction="up" in={open} mountOnEnter unmountOnExit>
        <Box className={classes.container}>
          <Box className={classes.header}>
            <Typography className={classes.headerTitle}>PDFのフォント埋め込み方法</Typography>
            <IconButton
              onClick={onClose}
              className={classes.closeButton}
              disableRipple
              disableFocusRipple
              aria-label="close"
            >
              <SvgIcon variant="close" size="15px" color={Colors.functional.whiteText.default} />
            </IconButton>
          </Box>
          <Box className={classes.body}>
            <Box className={classes.section}>
              <Box className={classes.sectionTitle}>なぜフォントの埋め込みが必要なのか？</Box>
              <Typography variant="body2" lineheight="1.8" className={classes.sectionBody}>
                フォントが埋め込まれていないPDFは、閲覧する環境に該当のフォントがインストールされていない場合、文字化けや表示崩れが発生します。すべてのユーザーが正しく資料を閲覧できるよう、フォントを埋め込んだPDFの作成をお願いいたします。
              </Typography>
            </Box>

            <Box className={classes.section}>
              <Box className={classes.sectionTitle}>ソフトウェア別の設定方法</Box>

              <Box className={classes.methodBox}>
                <Typography variant="subtitle2" className={classes.methodTitle}>
                  Adobe Acrobat Pro
                </Typography>
                <Typography variant="body2" className={classes.methodContent} lineheight="1.6">
                  1. 「ファイル」→「その他の形式で保存」を選択
                  <br />
                  2. 「アーカイブ可能なPDF（PDF/A）」を選択
                  <br />
                  3. 「PDF/A-1b」または「PDF/A-2b」を選択
                  <br />
                  4. 「保存」をクリック
                </Typography>
              </Box>

              <Box className={classes.methodBox}>
                <Typography variant="subtitle2" className={classes.methodTitle}>
                  Microsoft Word / PowerPoint
                </Typography>
                <Typography variant="body2" className={classes.methodContent} lineheight="1.6">
                  1. 「ファイル」→「名前を付けて保存」を選択
                  <br />
                  2. ファイルの種類で「PDF」を選択
                  <br />
                  3. 「オプション」をクリック
                  <br />
                  4. 「ISO 19005-1 に準拠（PDF/A）」にチェック
                  <br />
                  5. 「OK」→「保存」をクリック
                </Typography>
              </Box>

              <Box className={classes.methodBox}>
                <Typography variant="subtitle2" className={classes.methodTitle}>
                  Google ドキュメント / スライド
                </Typography>
                <Typography variant="body2" className={classes.methodContent} lineheight="1.6">
                  1. 「ファイル」→「ダウンロード」を選択
                  <br />
                  2. 「PDFドキュメント（.pdf）」を選択
                  <br />
                  ※Googleのエクスポート機能は自動的にフォントを埋め込みます
                </Typography>
              </Box>

              <Box className={classes.methodBox}>
                <Typography variant="subtitle2" className={classes.methodTitle}>
                  ブラウザでの変換
                </Typography>
                <Typography variant="body2" className={classes.methodContent} lineheight="1.6">
                  1. PDFをChromeまたはEdgeで開く
                  <br />
                  2. Ctrl/Cmd + P（印刷ダイアログを開く）
                  <br />
                  3. 「送信先」で「PDFに保存」を選択
                  <br />
                  4. 「保存」をクリック
                </Typography>
              </Box>

              <Box className={classes.methodBox}>
                <Typography variant="subtitle2" className={classes.methodTitle}>
                  PDFをフラット化する方法
                </Typography>
                <Typography variant="body2" className={classes.methodContent} lineheight="1.6">
                  1. Adobe Acrobat Proで「印刷工程」を選択
                  <br />
                  2. 「フラットナープレビュー」を開く
                  <br />
                  3. 「すべてフラット化」をクリック
                  <br />
                  <br />
                  または、Adobe
                  Acrobatで「ファイル」→「その他の形式で保存」→「最適化されたPDF」を選択し、「透明部分を統合」にチェック
                </Typography>
              </Box>

              <Box className={classes.tipBox}>
                <Typography variant="subtitle2" className={classes.tipTitle}>
                  💡 ヒント
                </Typography>
                <Typography variant="body2" className={classes.tipContent} lineheight="1.6">
                  フォントを埋め込む代わりに、PDFを画像化（フラット化）することでも問題を解決できます。ただし、画像化するとテキスト検索ができなくなる点にご注意ください。
                </Typography>
              </Box>
            </Box>

            <Box className={classes.section}>
              <Box className={classes.sectionTitle}>確認方法</Box>
              <Typography variant="body2" className={classes.sectionBody} lineheight="1.8">
                Adobe Acrobat
                Readerで「ファイル」→「プロパティ」→「フォント」タブを開き、すべてのフォントに「（埋め込みサブセット）」または「（埋め込み）」と表示されていることを確認してください。
              </Typography>
            </Box>
          </Box>
          <Box className={classes.footer}>
            <Button variant="contained" onClick={onClose} className={classes.closeButtonStyle} disableElevation>
              閉じる
            </Button>
          </Box>
        </Box>
      </Slide>
    </Modal>
  )
}

export default ModalPdfFontGuide
